import type { AnyObject } from '@/common';
import { useCallback } from 'react';

import type { GetUIElementByType } from '@ballerine/common';

import type { ITask } from '../../../../providers/TaskRunner/types';
import { useDocument, useDocumentFile } from '@/components/organisms/Form/DocumentsService';
import { useDynamicForm } from '../../../../context';
import { useElementId } from '../../../../hooks/external';
import { useTaskRunner } from '../../../../providers/TaskRunner/hooks/useTaskRunner';
import { useStack } from '../../../FieldList';
import { useCreateDocument } from '../useCreateDocument';
import { useDeleteDocumentFiles } from '../useDeleteDocument';
import { useReuploadDocument } from '../useReuploadDocument';

export const useDocumentUpload = (
  element: GetUIElementByType<'documentfield'>,
  params: GetUIElementByType<'documentfield'>['params'],
) => {
  const { metadata } = useDynamicForm();
  const { uploadOn = 'change' } = params;
  const { addTask, removeTask } = useTaskRunner();
  const { stack } = useStack();
  const id = useElementId(element, stack);
  const document = useDocument({
    type: element.params?.template?.type,
    category: element.params?.template?.category,
    entityType: 'business',
    entityId: metadata.businessId!,
  });
  const { setFile } = useDocumentFile({
    type: element.params?.template?.type,
    category: element.params?.template?.category,
    entityType: 'business',
    entityId: metadata.businessId!,
  });
  const { createDocument, isCreatingDocument } = useCreateDocument({
    element,
    entityType: 'business',
    entityId: metadata.businessId!,
  });
  const { reuploadDocument, isReuploadingDocument } = useReuploadDocument({
    element,
    entityType: 'business',
    entityId: metadata.businessId!,
  });
  const { deleteDocumentFiles, isDeletingDocumentFiles } = useDeleteDocumentFiles();

  const handleChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      removeTask(id);

      const file = e.target?.files?.[0]!;

      setFile(file);

      if (document) {
        void deleteDocumentFiles(document.id);
      }

      if (uploadOn === 'change') {
        try {
          document
            ? await reuploadDocument(e.target?.files?.[0]!)
            : await createDocument(e.target?.files?.[0]!);
        } catch (error) {
          console.error('Failed to upload file.', error);

          throw error;
        }
      }

      if (uploadOn === 'submit') {
        const taskRun = async (context: AnyObject) => {
          try {
            if (document) {
              await reuploadDocument(e.target?.files?.[0]!);
            } else {
              await createDocument(e.target?.files?.[0]!);
            }

            return context;
          } catch (error) {
            console.error('Failed to upload file.', error, element);

            throw error;
          }
        };

        const task: ITask = {
          id,
          element,
          run: taskRun,
        };
        addTask(task);
      }
    },
    [
      uploadOn,
      metadata,
      addTask,
      removeTask,
      id,
      element,
      document,
      createDocument,
      reuploadDocument,
      deleteDocumentFiles,
    ],
  );

  return {
    isUploading: isCreatingDocument || isReuploadingDocument || isDeletingDocumentFiles,
    handleChange,
  };
};
